import './CompanyDetails.css';
import React from 'react';
import { useRemoteData } from '../../../model/data';
import { getCompanyDetails } from '../../../model/company';
import { useRouterParam } from '../../../model/history';
import { Grid } from '../../../components/Grid/Grid';
import { Container } from '../../../components/Container/Container';
import { useMeta, getPageTitle } from '../../../model/meta';
import { companiesPath } from '../../../routes';
import { dig, useReloadKey } from '../../../util/util';
import { CompanyGeneralInfo } from './CompanyGeneralInfo';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { t } from '../../../locales/translate';
import { Activities } from '../../../components/Activities/Activities';
import { Stack } from '../../../components/Stack/Stack';
import { BillingAddress } from './BillingAddress';
import { AccountsPaymentsTaxes } from './AccountsPaymentsTaxes';
import { Memberships } from './Memberships';

export const CompanyDetails: React.FunctionComponent = () => {
  const id = useRouterParam<{ id: string }>().id;

  const [activitiesKey, reloadActivities] = useReloadKey();

  const [company, loading, error] = useRemoteData(
    async () => await getCompanyDetails(id),
    (db) => {
      return db.table('companies').where({ slug: id })[0] || db.table('companies').where({ id })[0];
    },
    [id]
  );

  useMeta({
    title: getPageTitle(loading, dig(company, 'name'), company),
    breadcrumbs: [
      { title: t.model.company._plural, url: companiesPath() },
      {
        title: getPageTitle(loading, dig(company, 'name'), company),
      },
    ],
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.company._name)} />;
  } else if (error || !company) {
    return <Container>{t.errors.itemNotFound(t.model.company._name)}</Container>;
  }

  return (
    <Container className="company-details">
      <Stack size="lg">
        <CompanyGeneralInfo company={company} />
        <Grid>
          <BillingAddress company={company} />
          <AccountsPaymentsTaxes company={company} />
        </Grid>
        <Memberships company={company} onReloadActivities={reloadActivities} />
        <Activities subjectType="company" subjectId={company.id} cacheKey={activitiesKey} />
      </Stack>
    </Container>
  );
};
